/**
 * src/components/shared/workbenchOrder.js
 *
 * Default row ordering for the shared Workbench
 * (src/components/shared/Workbench.jsx).
 *
 *
 * A register with no sort applied renders its rows in whatever order the data
 * source handed them over. For every collection that has moved to a per-record
 * store that is row-id order — `ORDER BY entity_id` over client-generated
 * UUIDs — which is, to a user, no order at all.
 *
 * That is invisible on a small register and actively broken on a large one: a
 * user saves a record, reloads, and the thing they just made is sitting at row
 * 139 of 167 with nothing to suggest where to look. The register is also
 * virtualized above 50 rows, so it is not merely far down the page — it is not
 * in the document at all until they scroll to it. "I saved it and now I can't
 * find it" is indistinguishable, from where the user is standing, from "it
 * wasn't saved", which is the confusion this ordering caused for real
 * (#4965 / #5362).
 *
 * So the default order is newest-first, derived from the rows themselves
 * rather than from a guess about which column means "recent". A register whose
 * records carry no creation timestamp — a catalogue view like SoA or
 * obligations, whose rows are derived from a control list and have a
 * meaningful natural order — is left exactly as its consumer supplied it.
 *
 * This sets the BASE order only. The moment a user sorts by a column, TanStack
 * sorts on top of this and wins; nothing here touches the sort state, so no
 * column header ever claims a sort the user did not choose.
 */

const RECENCY_KEYS = ['createdAt', 'created', 'createdDate'];

// Consumers build their rows as an explicit projection of the domain record
// and carry the record itself under `record` (the shape assetsWorkbenchColumns
// and biaWorkbenchColumns already read through). Those projections list only
// the fields their columns render, so `createdAt` is usually absent from the
// row and present on the record — buildAssetRows is exactly that case. Read
// through to the record so a register does not silently lose its ordering just
// because its projection has no reason to name a timestamp.
function recencyValue(row, key) {
  if (!row || typeof row !== 'object') return null;
  if (row[key] != null) return row[key];
  const record = row.record;
  if (record && typeof record === 'object' && record[key] != null) return record[key];
  return null;
}

// Scan for the first key that actually parses, rather than trusting row 0.
// A register's rows are homogeneous in principle, but a single record with a
// missing or malformed timestamp at the top must not silently switch the whole
// list back to store order — that is the failure mode this ordering exists to
// remove, reintroduced by one bad row.
const RECENCY_DETECT_SCAN = 25;

function detectRecencyKey(rows) {
  const limit = Math.min(rows.length, RECENCY_DETECT_SCAN);
  for (const key of RECENCY_KEYS) {
    for (let i = 0; i < limit; i += 1) {
      const value = recencyValue(rows[i], key);
      if (value != null && !Number.isNaN(Date.parse(value))) return key;
    }
  }
  return null;
}

/**
 * Return `rows` ordered newest-first, or the same reference when the rows
 * carry no usable timestamp. Never mutates the caller's array. Ties and
 * unparseable values keep their original relative position, so the order is
 * stable and a register never reshuffles between renders.
 */
export function orderRowsNewestFirst(rows) {
  if (!Array.isArray(rows) || rows.length < 2) return rows;
  const key = detectRecencyKey(rows);
  if (!key) return rows;
  const decorated = rows.map((row, index) => {
    const value = recencyValue(row, key);
    const t = value != null ? Date.parse(value) : NaN;
    return { row, index, t: Number.isNaN(t) ? null : t };
  });
  decorated.sort((a, b) => {
    if (a.t === b.t) return a.index - b.index;
    if (a.t === null) return 1; // undated rows sink, keeping their own order
    if (b.t === null) return -1;
    return b.t - a.t;
  });
  return decorated.map((d) => d.row);
}
